import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { tskService, bibleService } from '../../../services/bibleService';

// Map of TSK abbreviations → full book names
const BOOK_ABBREV_MAP = {
  // Old Testament
  'gen': 'Genesis', 'ge': 'Genesis', 'gn': 'Genesis',
  'ex': 'Exodus', 'exo': 'Exodus', 'exod': 'Exodus',
  'lev': 'Leviticus', 'le': 'Leviticus', 'lv': 'Leviticus',
  'num': 'Numbers', 'nu': 'Numbers', 'nm': 'Numbers', 'nb': 'Numbers',
  'deut': 'Deuteronomy', 'deu': 'Deuteronomy', 'de': 'Deuteronomy', 'dt': 'Deuteronomy',
  'josh': 'Joshua', 'jos': 'Joshua', 'jsh': 'Joshua',
  'judg': 'Judges', 'jdg': 'Judges', 'jg': 'Judges',
  'ruth': 'Ruth', 'rth': 'Ruth', 'ru': 'Ruth',
  '1sam': '1 Samuel', '1sa': '1 Samuel', '1 sam': '1 Samuel', '1 sa': '1 Samuel',
  '2sam': '2 Samuel', '2sa': '2 Samuel', '2 sam': '2 Samuel', '2 sa': '2 Samuel',
  '1kgs': '1 Kings', '1ki': '1 Kings', '1 kgs': '1 Kings', '1 ki': '1 Kings',
  '2kgs': '2 Kings', '2ki': '2 Kings', '2 kgs': '2 Kings', '2 ki': '2 Kings',
  '1chr': '1 Chronicles', '1ch': '1 Chronicles', '1 chr': '1 Chronicles', '1 ch': '1 Chronicles',
  '2chr': '2 Chronicles', '2ch': '2 Chronicles', '2 chr': '2 Chronicles', '2 ch': '2 Chronicles',
  'ezra': 'Ezra', 'ezr': 'Ezra',
  'neh': 'Nehemiah', 'ne': 'Nehemiah',
  'est': 'Esther', 'esth': 'Esther', 'es': 'Esther',
  'job': 'Job', 'jb': 'Job',
  'ps': 'Psalms', 'psa': 'Psalms', 'psm': 'Psalms', 'pss': 'Psalms', 'psalm': 'Psalms',
  'prov': 'Proverbs', 'pro': 'Proverbs', 'prv': 'Proverbs', 'pr': 'Proverbs',
  'eccl': 'Ecclesiastes', 'ecc': 'Ecclesiastes', 'ec': 'Ecclesiastes', 'qoh': 'Ecclesiastes',
  'song': 'Song of Solomon', 'sol': 'Song of Solomon', 'sos': 'Song of Solomon', 'ss': 'Song of Solomon', 'sng': 'Song of Solomon', 'cant': 'Song of Solomon',
  'isa': 'Isaiah', 'is': 'Isaiah',
  'jer': 'Jeremiah', 'je': 'Jeremiah', 'jr': 'Jeremiah',
  'lam': 'Lamentations', 'la': 'Lamentations',
  'ezek': 'Ezekiel', 'eze': 'Ezekiel', 'ezk': 'Ezekiel',
  'dan': 'Daniel', 'da': 'Daniel', 'dn': 'Daniel',
  'hos': 'Hosea', 'ho': 'Hosea',
  'joel': 'Joel', 'jl': 'Joel',
  'amos': 'Amos', 'am': 'Amos',
  'obad': 'Obadiah', 'ob': 'Obadiah',
  'jonah': 'Jonah', 'jon': 'Jonah',
  'mic': 'Micah', 'mc': 'Micah',
  'nah': 'Nahum', 'na': 'Nahum',
  'hab': 'Habakkuk', 'hb': 'Habakkuk',
  'zeph': 'Zephaniah', 'zep': 'Zephaniah', 'zp': 'Zephaniah',
  'hag': 'Haggai', 'hg': 'Haggai',
  'zech': 'Zechariah', 'zec': 'Zechariah', 'zc': 'Zechariah',
  'mal': 'Malachi', 'ml': 'Malachi',
  // New Testament
  'matt': 'Matthew', 'mat': 'Matthew', 'mt': 'Matthew',
  'mark': 'Mark', 'mar': 'Mark', 'mrk': 'Mark', 'mk': 'Mark', 'mr': 'Mark',
  'luke': 'Luke', 'luk': 'Luke', 'lk': 'Luke', 'lu': 'Luke',
  'john': 'John', 'joh': 'John', 'jn': 'John', 'jno': 'John',
  'acts': 'Acts', 'act': 'Acts', 'ac': 'Acts',
  'rom': 'Romans', 'ro': 'Romans', 'rm': 'Romans',
  '1cor': '1 Corinthians', '1co': '1 Corinthians', '1 cor': '1 Corinthians', '1 co': '1 Corinthians',
  '2cor': '2 Corinthians', '2co': '2 Corinthians', '2 cor': '2 Corinthians', '2 co': '2 Corinthians',
  'gal': 'Galatians', 'ga': 'Galatians',
  'eph': 'Ephesians',
  'phil': 'Philippians', 'php': 'Philippians', 'phip': 'Philippians',
  'col': 'Colossians',
  '1thess': '1 Thessalonians', '1th': '1 Thessalonians', '1 thess': '1 Thessalonians', '1 th': '1 Thessalonians',
  '2thess': '2 Thessalonians', '2th': '2 Thessalonians', '2 thess': '2 Thessalonians', '2 th': '2 Thessalonians',
  '1tim': '1 Timothy', '1ti': '1 Timothy', '1 tim': '1 Timothy', '1 ti': '1 Timothy',
  '2tim': '2 Timothy', '2ti': '2 Timothy', '2 tim': '2 Timothy', '2 ti': '2 Timothy',
  'titus': 'Titus', 'tit': 'Titus',
  'philem': 'Philemon', 'phm': 'Philemon', 'pm': 'Philemon',
  'heb': 'Hebrews', 'he': 'Hebrews',
  'jas': 'James', 'jm': 'James',
  '1pet': '1 Peter', '1pe': '1 Peter', '1pt': '1 Peter', '1 pet': '1 Peter', '1 pe': '1 Peter',
  '2pet': '2 Peter', '2pe': '2 Peter', '2pt': '2 Peter', '2 pet': '2 Peter', '2 pe': '2 Peter',
  '1john': '1 John', '1jn': '1 John', '1jo': '1 John', '1 john': '1 John', '1 jn': '1 John',
  '2john': '2 John', '2jn': '2 John', '2jo': '2 John', '2 john': '2 John', '2 jn': '2 John',
  '3john': '3 John', '3jn': '3 John', '3jo': '3 John', '3 john': '3 John', '3 jn': '3 John',
  'jude': 'Jude', 'jud': 'Jude',
  'rev': 'Revelation', 're': 'Revelation', 'rv': 'Revelation',
};

/**
 * Expand a TSK short-code reference like "isa 21:3" → "Isaiah 21:3" * Handles numbered books:"1 cor 13:4", "1cor 13:4"
 */
function expandBookAbbrev(ref) {
  if (!ref) return ref;
  const trimmed = ref?.trim();

  const match = trimmed?.match(/^(\d\s?)?([a-zA-Z]+)\s*(\d.*)$/);
  if (!match) return trimmed;

  const prefix = match?.[1] ? match?.[1]?.trim() : '';
  const bookPart = match?.[2]?.toLowerCase();
  const rest = match?.[3];

  const lookupKey = prefix ? `${prefix}${bookPart}` : bookPart;
  const lookupKeySpaced = prefix ? `${prefix} ${bookPart}` : bookPart;

  const fullName =
    BOOK_ABBREV_MAP?.[lookupKey] ||
    BOOK_ABBREV_MAP?.[lookupKeySpaced] ||
    BOOK_ABBREV_MAP?.[bookPart];

  if (fullName) {
    return `${fullName} ${rest}`;
  }

  const capitalized = trimmed?.replace(/\b\w/g, c => c?.toUpperCase());
  return capitalized;
}

/**
 * Parse a fully-expanded reference string like "Psalms 104:2-5"
 * Returns { bookName, chapter, startVerse, endVerse } or null
 */
function parseExpandedRef(ref) {
  if (!ref) return null;
  // Match: "Book Name 10:2-5" or "Book Name 10:2"
  const match = ref?.match(/^(.+?)\s+(\d+):(\d+)(?:-(\d+))?$/);
  if (!match) return null;
  return {
    bookName: match?.[1],
    chapter: parseInt(match?.[2]),
    startVerse: parseInt(match?.[3]),
    endVerse: match?.[4] ? parseInt(match?.[4]) : parseInt(match?.[3]),
  };
}

/**
 * Verse Tooltip component — fetches and shows verse text on hover
 */
const VerseTooltip = ({ refObj, textSizePx, onClickRef }) => {
  const [hovered, setHovered] = useState(false);
  const [verseText, setVerseText] = useState(null);
  const [loadingText, setLoadingText] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const hoverTimer = useRef(null);
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);

  const parsed = parseExpandedRef(refObj?.ref);
  const isRange = parsed && parsed?.endVerse > parsed?.startVerse;

  const handleMouseEnter = () => {
    hoverTimer.current = setTimeout(async () => {
      setHovered(true);
      if (!verseText && parsed) {
        setLoadingText(true);
        try {
          const { data } = await bibleService?.getVerseByReference(
            parsed?.bookName,
            parsed?.chapter,
            parsed?.startVerse
          );
          if (data?.text) {
            setVerseText(data?.text);
          } else {
            setVerseText(null);
          }
        } catch {
          setVerseText(null);
        } finally {
          setLoadingText(false);
        }
      }
    }, 300);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimer?.current);
    setHovered(false);
  };

  // Reposition tooltip using fixed positioning relative to viewport
  useEffect(() => {
    if (!hovered || !triggerRef?.current) return;

    const trigger = triggerRef?.current;
    const triggerRect = trigger?.getBoundingClientRect();

    const TOOLTIP_WIDTH = 288; // w-72
    const PADDING = 8;
    const VIEWPORT_WIDTH = window.innerWidth;
    const VIEWPORT_HEIGHT = window.innerHeight;

    // Horizontal: try to center on trigger, clamp to viewport
    let left = triggerRect?.left + triggerRect?.width / 2 - TOOLTIP_WIDTH / 2;
    if (left + TOOLTIP_WIDTH > VIEWPORT_WIDTH - PADDING) {
      left = VIEWPORT_WIDTH - PADDING - TOOLTIP_WIDTH;
    }
    if (left < PADDING) left = PADDING;

    // Estimate tooltip height for positioning (use 120px as safe estimate before render)
    const tooltipHeight = tooltipRef?.current?.getBoundingClientRect()?.height || 120;
    const spaceAbove = triggerRect?.top;
    const spaceBelow = VIEWPORT_HEIGHT - triggerRect?.bottom;

    let top;
    if (spaceAbove >= tooltipHeight + PADDING) {
      top = triggerRect?.top - tooltipHeight - 6;
    } else if (spaceBelow >= tooltipHeight + PADDING) {
      top = triggerRect?.bottom + 6;
    } else {
      top = spaceAbove > spaceBelow
        ? Math.max(PADDING, triggerRect?.top - tooltipHeight - 6)
        : Math.min(VIEWPORT_HEIGHT - tooltipHeight - PADDING, triggerRect?.bottom + 6);
    }

    setTooltipStyle({ left: `${left}px`, top: `${top}px` });
  }, [hovered, verseText, loadingText]);

  const displayLabel = isRange
    ? `${refObj?.ref?.replace(/:\d+$/, '')}:${parsed?.startVerse}-${parsed?.endVerse}`
    : refObj?.ref;

  const tooltipContent = hovered ? createPortal(
    <div
      ref={tooltipRef}
      className="fixed z-[99999] w-72 bg-popover text-popover-foreground border border-border rounded-lg shadow-xl p-3 pointer-events-none"
      style={{ minWidth: '220px', maxWidth: '288px', ...tooltipStyle }}
    >
      {/* Reference header */}
      <div className="font-semibold text-amber-500 mb-1.5" style={{ fontSize: textSizePx || '12px' }}>
        {displayLabel}
        {isRange && (
          <span className="ml-1.5 text-muted-foreground font-normal">(range — navigates to v.{parsed?.startVerse})</span>
        )}
      </div>
      {/* Verse text */}
      {loadingText ? (
        <div className="flex items-center gap-1.5 text-muted-foreground" style={{ fontSize: textSizePx || '12px' }}>
          <Icon name="Loader2" size={12} className="animate-spin" />
          Loading verse...
        </div>
      ) : verseText ? (
        <p className="text-foreground leading-relaxed italic" style={{ fontSize: textSizePx || '12px' }}>"{verseText}"</p>
      ) : (
        <p className="text-muted-foreground italic" style={{ fontSize: textSizePx || '12px' }}>Verse text unavailable</p>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative inline-block" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        ref={triggerRef}
        onClick={() => onClickRef(refObj)}
        className="text-amber-500 hover:text-amber-400 hover:underline px-2 py-1 bg-card rounded border border-border hover:bg-muted transition-colors"
        style={{ fontSize: textSizePx || '12px' }}
        title={isRange ? `Range: ${displayLabel} — links to verse ${parsed?.startVerse}` : `Go to ${refObj?.ref}`}
      >
        {displayLabel}
      </button>
      {tooltipContent}
    </div>
  );
};

const TSKDisplay = ({ 
  book, 
  chapter, 
  verse, 
  verseId,
  isVisible, 
  onToggleVisibility, 
  onVerseReference,
  textSizePx,
  className = '' 
}) => {
  const [tskData, setTskData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const effectiveFontSize = textSizePx || '12px';

  // Load TSK data when component becomes visible
  useEffect(() => {
    if (isVisible && book && chapter && verse) {
      loadTSKData();
    }
  }, [isVisible, book, chapter, verse]);

  const loadTSKData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('TSKDisplay - Loading TSK data for:', { book, chapter, verse });
      
      if (!book || typeof book !== 'string' || book?.length < 2) {
        console.error('Invalid book name passed to TSKDisplay:', book);
        setError('Invalid book reference');
        return;
      }

      const { data, error: fetchError } = await tskService?.getTSKForVerse(book, chapter, verse);

      if (fetchError) {
        setError('Failed to load TSK data');
        console.error('Error loading TSK data:', fetchError);
      } else if (!data || data?.length === 0) {
        setError('No Treasury of Scripture Knowledge data available for this verse.');
        setTskData([]);
      } else {
        setTskData(data);
      }
    } catch (err) {
      setError('Failed to load TSK data');
      console.error('Error loading TSK data:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Parse TSK references — handles comma-delimited verses AND verse ranges (e.g. "Ps 104:2-5")
   * For ranges: link to first verse, but carry range info for highlighting
   */
  const parseReferences = (referencesString) => {
    if (!referencesString) return [];

    const groups = [];
    const semicolonSplit = referencesString?.split(';');

    semicolonSplit?.forEach((group) => {
      const trimmedGroup = group?.trim();
      if (!trimmedGroup) return;

      // Check if this reference has comma-separated verses (e.g., "Ps 33:6,9")
      const colonMatch = trimmedGroup?.match(/^(.+?):(\d+(?:,\d+)+)$/);
      
      if (colonMatch) {
        // Has comma-separated verses after colon
        const bookChapter = colonMatch?.[1]?.trim();
        const verses = colonMatch?.[2]?.split(',')?.map(v => v?.trim());
        
        const expandedRefs = verses?.map(verseNum => {
          const expanded = expandBookAbbrev(`${bookChapter}:${verseNum}`);
          return {
            ref: expanded,
            original: trimmedGroup,
            isGrouped: true,
            isRange: false,
          };
        });
        
        groups?.push({
          references: expandedRefs,
          original: trimmedGroup
        });
      } else {
        // Single reference — may contain a range like "Ps 104:2-5"
        const expanded = expandBookAbbrev(trimmedGroup);
        // Detect range in the expanded ref
        const rangeMatch = expanded?.match(/^(.+?\s+\d+):(\d+)-(\d+)$/);
        if (rangeMatch) {
          // It's a range: "Book Chapter:start-end"
          const bookChapterPart = rangeMatch?.[1]; // e.g. "Psalms 104"
          const startV = parseInt(rangeMatch?.[2]);
          const endV = parseInt(rangeMatch?.[3]);
          // Link ref = first verse only
          const linkRef = `${bookChapterPart}:${startV}`;
          groups?.push({
            references: [{
              ref: linkRef,
              displayRef: expanded, // show the full range as label
              original: trimmedGroup,
              isGrouped: false,
              isRange: true,
              rangeStart: startV,
              rangeEnd: endV,
            }],
            original: trimmedGroup
          });
        } else {
          groups?.push({
            references: [{ ref: expanded, original: trimmedGroup, isGrouped: false, isRange: false }],
            original: trimmedGroup
          });
        }
      }
    });

    return groups;
  };

  const handleReferenceClick = (refObj) => {
    if (onVerseReference) {
      if (refObj?.isRange) {
        // Pass the link ref (first verse) but also include range info
        onVerseReference(refObj?.ref, {
          isRange: true,
          rangeStart: refObj?.rangeStart,
          rangeEnd: refObj?.rangeEnd,
          displayRef: refObj?.displayRef,
        });
      } else {
        onVerseReference(refObj?.ref);
      }
    }
  };

  if (!isVisible) return null;

  return (
    <div className={`bg-amber-500/5 border border-amber-500/20 rounded-lg ${className}`}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Icon name="GitBranch" size={16} className="text-amber-500" />
            <h4 className="font-semibold text-foreground" style={{ fontSize: effectiveFontSize }}>Treasury of Scripture Knowledge</h4>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={onToggleVisibility}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={16} />
          </Button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center space-x-2 text-amber-500 py-4">
            <Icon name="Loader2" size={16} className="animate-spin" />
            <span style={{ fontSize: effectiveFontSize }}>Loading cross-references...</span>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-4">
            <div className="flex items-center justify-center space-x-2 text-yellow-600 mb-2">
              <Icon name="AlertCircle" size={16} />
              <span style={{ fontSize: effectiveFontSize }} className="font-medium">{error}</span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadTSKData}
              className="text-amber-500 border-amber-500/30 hover:bg-amber-500/10"
            >
              <Icon name="RefreshCw" size={14} className="mr-1" />
              Retry
            </Button>
          </div>
        )}

        {/* TSK Content */}
        {!loading && !error && tskData?.length > 0 && (
          <div className="space-y-4">
            {tskData?.map((entry, index) => {
              const referenceGroups = parseReferences(entry?.references);
              
              return (
                <div key={entry?.id || index} className="border-l-4 border-amber-500 pl-4">
                  {/* Phrase - Bold */}
                  <h5 className="font-bold text-foreground mb-2" style={{ fontSize: effectiveFontSize }}>
                    {entry?.phrase}
                  </h5>
                  
                  {/* References - Grouped with hover tooltips */}
                  <div className="flex flex-wrap gap-2">
                    {referenceGroups?.map((group, groupIndex) => (
                      <div key={groupIndex} className="flex flex-wrap gap-1">
                        {group?.references?.map((refObj, refIndex) => (
                          <VerseTooltip
                            key={refIndex}
                            refObj={refObj}
                            textSizePx={textSizePx}
                            onClickRef={handleReferenceClick}
                          />
                        ))}
                      </div>
                    ))}
                  </div>

                  {/* Separator for multiple entries */}
                  {index < tskData?.length - 1 && (
                    <hr className="border-border mt-3" />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Info Footer */}
        {!loading && !error && tskData?.length > 0 && (
          <div className="mt-4 pt-3 border-t border-border">
            <div className="flex items-center" style={{ fontSize: '11px' }}>
              <Icon name="Info" size={12} className="mr-1 text-muted-foreground" />
              <span className="text-muted-foreground">Hover references to preview verse text · Click to navigate</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TSKDisplay;